import React, { useState, useMemo } from 'react';
import { Search, Download, Database, ExternalLink, Plus, CheckCircle, AlertCircle, Globe } from 'lucide-react';
import { ModuleInfoModal } from './ModuleInfoModal';
import ActiveProjectBadge from './ActiveProjectBadge';
import { useAppContext } from '../contexts/AppContext';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import {
    searchAllMaterialDatabases,
    convertToLocalMaterial,
    exportMaterialToSeedFormat,
    ExternalMaterial
} from '../services/materialDatabaseService';

const ExternalMaterialSearch: React.FC = () => {
    const { allData, activeProject } = useAppContext();

    // Project filtering - consistent with other pages
    const isItemInProject = (projName?: string, epochId?: number): boolean => {
        if (!activeProject || activeProject === 'all') return true;

        const activeNorm = normalizeProjectName(activeProject);
        const itemNorm = normalizeProjectName(projName || '');

        if (itemNorm && (itemNorm === activeNorm || itemNorm.includes(activeNorm) || activeNorm.includes(itemNorm))) {
            return true;
        }

        if (epochId) {
            const epoch = allData.iterationHistory?.find(e =>
                (e.epochNumber || e.iteration) === epochId
            );
            if (epoch) {
                const epochProj = extractProjectName(epoch.epochName || '');
                const epochNorm = normalizeProjectName(epochProj);
                if (epochNorm === activeNorm || epochNorm.includes(activeNorm)) {
                    return true;
                }
            }
        }

        return false;
    };

    // Get project-filtered materials from allData
    const projectMaterials = useMemo(() => {
        return (allData.materials || []).filter(m =>
            isItemInProject(m.projectName, m.epochId)
        );
    }, [allData.materials, activeProject]);

    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<{
        openMaterialsDB: ExternalMaterial[];
        matWeb: ExternalMaterial[];
        local: ExternalMaterial[];
    } | null>(null);
    const [selectedMaterial, setSelectedMaterial] = useState<ExternalMaterial | null>(null);
    const [importOptions, setImportOptions] = useState({
        supplier: '',
        costValue: 0,
        costCurrency: 'USD',
        leadTimeWeeks: 0,
        linkedBomIds: ''
    });
    const [exportedCode, setExportedCode] = useState('');

    const handleSearch = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!searchTerm.trim()) return;

        setIsSearching(true);
        setSearchResults(null);
        setExportedCode('');

        try {
            const results = await searchAllMaterialDatabases(searchTerm, projectMaterials);
            setSearchResults(results);
        } catch (error) {
            console.error('Search error:', error);
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectMaterial = (material: ExternalMaterial) => {
        setSelectedMaterial(material);
        setExportedCode('');
    };

    const handleImportMaterial = () => {
        if (!selectedMaterial) return;

        const bomIds = importOptions.linkedBomIds
            .split(',')
            .map(id => parseInt(id.trim()))
            .filter(id => !isNaN(id));

        const localMaterial = convertToLocalMaterial(selectedMaterial, {
            supplier: importOptions.supplier,
            cost: {
                value: importOptions.costValue,
                currency: importOptions.costCurrency
            },
            leadTimeWeeks: importOptions.leadTimeWeeks,
            linkedBomIds: bomIds
        });

        const code = exportMaterialToSeedFormat(localMaterial);
        setExportedCode(code);
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(exportedCode);
        alert('Material code copied to clipboard! Add it to materials-seed.ts');
    };

    const getTotalResults = () => {
        if (!searchResults) return 0;
        return searchResults.openMaterialsDB.length +
            searchResults.matWeb.length +
            searchResults.local.length;
    };

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
                    <Database className="text-primary" /> Materials Database
                </h1>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <p className="text-text-secondary mt-2">
                            Search external databases and import materials to your project
                        </p>
                        <ActiveProjectBadge showStandard={false} />
                        <ModuleInfoModal
                            title="Materials Database & Import"
                            description="Search and import materials from global databases (OpenMaterialsDB, MatWeb) into your local project."
                            benefits={[
                                "Access 150,000+ verified material properties.",
                                "One-click import to your project seed data or JSON.",
                                "Auto-convert units to project standards."
                            ]}
                            usageInstructions={[
                                "Type a material name (e.g., 'Aluminium 6061') and hit Search.",
                                "Select a result to view properties.",
                                "Configure supplier/cost and click 'Generate Import Code'."
                            ]}
                        />
                    </div>
                </div>

                {/* Search Bar */}
                <form onSubmit={handleSearch} className="mb-6">
                    <div className="flex gap-2">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary" size={20} />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search materials (e.g., 'stainless steel', 'ABS plastic', 'viton')"
                                className="w-full pl-10 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                                disabled={isSearching}
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={isSearching || !searchTerm.trim()}
                            className="px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                        >
                            {isSearching ? (
                                <>
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    Searching...
                                </>
                            ) : (
                                <>
                                    <Search size={20} />
                                    Search All Databases
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Database Sources Info */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Globe size={20} className="text-blue-600" />
                            <h3 className="font-semibold text-blue-900">OpenMaterialsDB</h3>
                        </div>
                        <p className="text-sm text-blue-700">Swedish materials database with comprehensive property data</p>
                        <a
                            href="https://openmaterialsdb.se"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-blue-600 hover:underline mt-2 inline-flex items-center gap-1"
                        >
                            Visit website <ExternalLink size={12} />
                        </a>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Globe size={20} className="text-green-600" />
                            <h3 className="font-semibold text-green-900">MatWeb</h3>
                        </div>
                        <p className="text-sm text-green-700">Large materials database with over 150,000 materials</p>
                        <a
                            href="https://www.matweb.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-green-600 hover:underline mt-2 inline-flex items-center gap-1"
                        >
                            Visit website <ExternalLink size={12} />
                        </a>
                    </div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Database size={20} className="text-purple-600" />
                            <h3 className="font-semibold text-purple-900">Local Database</h3>
                        </div>
                        <p className="text-sm text-purple-700">{projectMaterials.length} validated materials in {activeProject === 'all' ? 'all projects' : activeProject}</p>
                    </div>
                </div>

                {/* Search Results */}
                {searchResults && (
                    <div className="grid grid-cols-2 gap-6">
                        {/* Results List */}
                        <div>
                            <h2 className="text-xl font-bold mb-4">
                                Search Results ({getTotalResults()})
                            </h2>

                            {/* Local Results */}
                            {searchResults.local.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-semibold text-purple-700 mb-2 flex items-center gap-2">
                                        <Database size={16} /> Local Database ({searchResults.local.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {searchResults.local.map((mat) => (
                                            <button
                                                key={mat.id}
                                                onClick={() => handleSelectMaterial(mat)}
                                                className={`w-full text-left p-3 border rounded-lg transition-all ${selectedMaterial?.id === mat.id
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-border hover:border-primary/50'
                                                    }`}
                                            >
                                                <div className="font-semibold text-sm">{mat.name}</div>
                                                {mat.grade && <div className="text-xs text-text-secondary">Grade: {mat.grade}</div>}
                                                <div className="text-xs text-purple-600 mt-1">✓ Already in database</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* OpenMaterialsDB Results */}
                            {searchResults.openMaterialsDB.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-2">
                                        <Globe size={16} /> OpenMaterialsDB ({searchResults.openMaterialsDB.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {searchResults.openMaterialsDB.map((mat) => (
                                            <button
                                                key={mat.id}
                                                onClick={() => handleSelectMaterial(mat)}
                                                className={`w-full text-left p-3 border rounded-lg transition-all ${selectedMaterial?.id === mat.id
                                                    ? 'border-primary bg-primary/5'
                                                    : 'border-border hover:border-primary/50'
                                                    }`}
                                            >
                                                <div className="font-semibold text-sm">{mat.name}</div>
                                                {mat.grade && <div className="text-xs text-text-secondary">Grade: {mat.grade}</div>}
                                                <div className="text-xs text-blue-600 flex items-center gap-1 mt-1">
                                                    <ExternalLink size={12} /> External source
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* MatWeb Results */}
                            {searchResults.matWeb.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-green-700 mb-2 flex items-center gap-2">
                                        <Globe size={16} /> MatWeb ({searchResults.matWeb.length})
                                    </h3>
                                    <div className="space-y-2">
                                        {searchResults.matWeb.map((mat) => (
                                            <a
                                                key={mat.id}
                                                href={mat.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block p-3 border border-border rounded-lg hover:border-green-500 transition-all"
                                            >
                                                <div className="font-semibold text-sm">{mat.name}</div>
                                                <div className="text-xs text-text-secondary">{mat.description}</div>
                                                <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                                                    <ExternalLink size={12} /> Open in MatWeb
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {getTotalResults() === 0 && (
                                <div className="text-center py-12 text-text-secondary">
                                    <AlertCircle size={48} className="mx-auto mb-4 opacity-50" />
                                    <p>No materials found matching &quot;{searchTerm}&quot;</p>
                                    <p className="text-sm mt-2">Try different search terms or check external databases manually</p>
                                </div>
                            )}
                        </div>

                        {/* Material Details & Import */}
                        <div>
                            {selectedMaterial ? (
                                <div className="bg-card border border-border rounded-lg p-6">
                                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                        {selectedMaterial.source === 'Local' ? (
                                            <CheckCircle className="text-green-500" />
                                        ) : (
                                            <Plus className="text-blue-500" />
                                        )}
                                        {selectedMaterial.name}
                                    </h2>

                                    {selectedMaterial.grade && (
                                        <div className="mb-2">
                                            <span className="text-sm font-semibold">Grade:</span> {selectedMaterial.grade}
                                        </div>
                                    )}

                                    {selectedMaterial.standards && selectedMaterial.standards.length > 0 && (
                                        <div className="mb-4">
                                            <span className="text-sm font-semibold">Standards:</span> {selectedMaterial.standards.join(', ')}
                                        </div>
                                    )}

                                    <h3 className="font-semibold mb-2 text-sm">Material Properties</h3>
                                    <div className="space-y-1 mb-6 max-h-64 overflow-y-auto">
                                        {selectedMaterial.properties.length > 0 ? (
                                            selectedMaterial.properties.map((prop, idx) => (
                                                <div key={idx} className="flex justify-between text-sm bg-background/50 p-2 rounded">
                                                    <span className="text-text-secondary">{prop.name}:</span>
                                                    <span className="font-medium">{prop.value} {prop.unit || ''}</span>
                                                </div>
                                            ))
                                        ) : (
                                            <p className="text-sm text-text-secondary">No property data available</p>
                                        )}
                                    </div>

                                    {selectedMaterial.source !== 'Local' && selectedMaterial.source !== 'MatWeb' && (
                                        <>
                                            <h3 className="font-semibold mb-3 text-sm">Import to Local Database</h3>

                                            <div className="space-y-3 mb-4">
                                                <div>
                                                    <label className="text-xs font-semibold block mb-1">Supplier</label>
                                                    <input
                                                        type="text"
                                                        value={importOptions.supplier}
                                                        onChange={(e) => setImportOptions({ ...importOptions, supplier: e.target.value })}
                                                        placeholder="e.g., PlasticTech Inc"
                                                        className="w-full px-3 py-2 text-sm border border-border rounded"
                                                    />
                                                </div>

                                                <div className="grid grid-cols-2 gap-2">
                                                    <div>
                                                        <label className="text-xs font-semibold block mb-1">Cost Value</label>
                                                        <input
                                                            type="number"
                                                            value={importOptions.costValue}
                                                            onChange={(e) => setImportOptions({ ...importOptions, costValue: parseFloat(e.target.value) || 0 })}
                                                            placeholder="0.00"
                                                            step="0.01"
                                                            className="w-full px-3 py-2 text-sm border border-border rounded"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-xs font-semibold block mb-1">Currency</label>
                                                        <select
                                                            value={importOptions.costCurrency}
                                                            onChange={(e) => setImportOptions({ ...importOptions, costCurrency: e.target.value })}
                                                            className="w-full px-3 py-2 text-sm border border-border rounded"
                                                        >
                                                            <option>USD</option>
                                                            <option>EUR</option>
                                                            <option>GBP</option>
                                                            <option>SEK</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="text-xs font-semibold block mb-1">Lead Time (weeks)</label>
                                                    <input
                                                        type="number"
                                                        value={importOptions.leadTimeWeeks}
                                                        onChange={(e) => setImportOptions({ ...importOptions, leadTimeWeeks: parseInt(e.target.value) || 0 })}
                                                        placeholder="0"
                                                        className="w-full px-3 py-2 text-sm border border-border rounded"
                                                    />
                                                </div>

                                                <div>
                                                    <label className="text-xs font-semibold block mb-1">Linked BOM IDs (comma-separated)</label>
                                                    <input
                                                        type="text"
                                                        value={importOptions.linkedBomIds}
                                                        onChange={(e) => setImportOptions({ ...importOptions, linkedBomIds: e.target.value })}
                                                        placeholder="e.g., 2, 9, 16"
                                                        className="w-full px-3 py-2 text-sm border border-border rounded"
                                                    />
                                                </div>
                                            </div>

                                            <button
                                                onClick={handleImportMaterial}
                                                className="w-full bg-primary text-white py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
                                            >
                                                <Download size={20} />
                                                Generate Import Code
                                            </button>
                                        </>
                                    )}

                                    {selectedMaterial.url && (
                                        <a
                                            href={selectedMaterial.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="block mt-4 text-sm text-primary hover:underline flex items-center gap-1"
                                        >
                                            View in {selectedMaterial.source} <ExternalLink size={14} />
                                        </a>
                                    )}
                                </div>
                            ) : (
                                <div className="bg-card border border-border rounded-lg p-6 text-center text-text-secondary">
                                    <Database size={48} className="mx-auto mb-4 opacity-30" />
                                    <p>Select a material to view details and import options</p>
                                </div>
                            )}

                            {/* Export Code */}
                            {exportedCode && (
                                <div className="bg-card border border-green-200 rounded-lg p-6 mt-4">
                                    <h3 className="font-semibold mb-2 flex items-center gap-2 text-green-700">
                                        <CheckCircle size={20} />
                                        Material Ready to Import!
                                    </h3>
                                    <p className="text-sm text-text-secondary mb-3">
                                        Copy this code and add it to <code className="bg-background px-1 rounded">data/seed/materials-seed.ts</code>
                                    </p>
                                    <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto max-h-64 overflow-y-auto font-mono">
                                        {exportedCode}
                                    </pre>
                                    <button
                                        onClick={copyToClipboard}
                                        className="mt-3 w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors"
                                    >
                                        Copy to Clipboard
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExternalMaterialSearch;
